import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PaymentIframeModal, { PaymentBreakdown } from '../components/PaymentIframeModal';

const API_URL = (import.meta.env.VITE_API_URL || '/api') as string;

interface PlanMeta {
  sms: number;
  smsLabel: string;
  emails: number;
  users: number;
  receipts: number;
  contacts: string;
  features: string[];
}

const PLAN_META: Record<string, PlanMeta> = {
  basic: {
    sms: 500, smsLabel: 'חד פעמי',
    emails: 2000, users: 2, receipts: 100, contacts: 'עד 1,000',
    features: [
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה',
      'דוחות בסיסיים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב',
      'ניהול יומן משותף לעמותה',
      'ניהול מתנדבים',
    ],
  },
  pro: {
    sms: 1000, smsLabel: 'חודשי',
    emails: 4000, users: 4, receipts: 500, contacts: 'עד 5,000',
    features: [
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה מבוססי AI',
      'דוחות מפורטים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב כולל דוחות פיננסים',
      'ניהול יומן משותף ושותף לעמותה',
      'ניהול מתנדבים',
    ],
  },
  org: {
    sms: 2500, smsLabel: 'חודשי',
    emails: 10000, users: 10, receipts: 2200, contacts: 'ללא הגבלה',
    features: [
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה מבוססי AI',
      'דוחות מפורטים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב כולל דוחות פיננסים',
      'ניהול יומן משותף ושותף לעמותה',
      'מרכז ידע ומתעדכן',
      'ניהול מתנדבים',
    ],
  },
};

const DOCUMENT_TYPES = [
  { id: 'registration', name: 'תעודת רישום עמותה', required: true },
  { id: 'protocol', name: 'פרוטוקול מורשי חתימה', required: true },
  { id: 'management', name: 'אישור ניהול תקין', required: true },
  { id: 'bank', name: "אישור ניהול חשבון / צ'ק", required: true },
  { id: 'id', name: 'ת.ז. מורשי חתימה', required: true },
  { id: 'statutes', name: 'תקנון העמותה', required: false },
];

interface QuoteData {
  _id: string;
  targetOrgName: string;
  targetEmail: string;
  planId: string;
  planName: string;
  monthlyPrice: number;
  discount: number;
  discountMonths?: number;
  finalPrice: number;
  customNotes?: string;
  status: string;
  expiresAt: string;
  pdfUrl?: string;
  signedAt?: string;
  customSms?: number;
  customEmails?: number;
  customUsers?: number;
  customReceipts?: number;
  customFeatures?: Array<{ id: string; text: string; included: boolean }>;
  setupFee?: number;
  requirePayment?: boolean;
  documentsOptional?: boolean;
  skipGamaAndDocs?: boolean;
  oneTimePayment?: boolean;
}

const TOTAL_STEPS = 11;
const ADDON_PRICE = 15;

const DONOR_PAYMENT_ADDONS = [
  { id: 'googlePay', name: 'Google Pay', icon: '🟢', desc: 'תשלום מהיר לתורמים שלך דרך Google' },
  { id: 'applePay',  name: 'Apple Pay',  icon: '',  desc: 'תשלום מהיר לתורמים שלך דרך Apple' },
  { id: 'bit',       name: 'Bit',        icon: '🟡', desc: 'תשלום מאפליקציית Bit' },
] as const;

const PublicQuote: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    orgName: '',
    regNumber: '',
    address: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });

  // Documents
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, { name: string; url: string }>>({});
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  // GAMAF
  const [gamafFilled, setGamafFilled] = useState(false);

  // Terms
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Donor payment add-ons (org chooses what payment methods to offer THEIR donors)
  const [donorPaymentMethods, setDonorPaymentMethods] = useState({
    googlePay: false,
    applePay: false,
    bit: false,
  });

  // Org settings (logo, website, receipt numbering, fill-receipts authorization)
  const [orgSettings, setOrgSettings] = useState({
    logo: '',
    websiteUrl: '',
    receiptStartNumber: 4001,
    creditNoteStartNumber: 6001,
    issuesServiceReceipts: false,
    serviceReceiptStartNumber: 7001,
    receiptAuthorizationGranted: false,
  });

  // Payment confirmations
  const [cardSavedForRecurring, setCardSavedForRecurring] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeDetails, setIframeDetails] = useState<PaymentBreakdown | undefined>(undefined);

  // Signature
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  // Result
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load quote
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/quotes/public/${token}`);
        setQuote(res.data);
        setFormData((f) => ({
          ...f,
          orgName: res.data.targetOrgName,
          contactEmail: res.data.targetEmail,
        }));
      } catch (err: any) {
        setError(err.response?.data?.error || 'הצעת המחיר לא נמצאה');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Mark viewed when step 1 loads
  useEffect(() => {
    if (quote && step === 1) {
      axios.post(`${API_URL}/quotes/public/${token}/view`).catch(() => {});
    }
  }, [quote]);

  // Defensive: never show the Tranzila step if the quote is marked as no-payment.
  useEffect(() => {
    if (step === 10 && quote?.requirePayment === false) {
      setStep(11);
    }
  }, [step, quote?.requirePayment]);

  // Canvas drawing
  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: (e as React.MouseEvent).clientX - rect.left,
      y: (e as React.MouseEvent).clientY - rect.top,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current!;
    const pos = getPos(e, canvas);
    setIsDrawing(true);
    setLastPos(pos);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();
    setLastPos(pos);
    setHasSignature(true);
  };

  const stopDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Document upload
  const handleDocUpload = async (docId: string, file: File) => {
    setUploadingDoc(docId);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      // Use public endpoint without auth
      const res = await axios.post(`${API_URL}/upload/quote-document`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedDocs((prev) => ({
        ...prev,
        [docId]: { name: file.name, url: res.data.url },
      }));
    } catch {
      alert('שגיאה בהעלאת המסמך');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Submit form data
  const handleSubmitData = async () => {
    setSubmitting(true);
    try {
      const documents = Object.entries(uploadedDocs).map(([type, doc]) => ({
        type,
        name: doc.name,
        url: doc.url,
        uploadedAt: new Date().toISOString(),
      }));

      await axios.post(`${API_URL}/quotes/public/${token}/submit`, {
        filledData: {
          ...formData,
          gamafFilled,
        },
        documents,
        donorPaymentMethods,
        orgSettings,
      });
    } catch {
      // silent — continue
    } finally {
      setSubmitting(false);
    }
  };

  // Sign
  const handleSign = async () => {
    if (!hasSignature) {
      alert('נא לחתום לפני האישור');
      return;
    }
    const canvas = canvasRef.current!;
    const signatureDataUrl = canvas.toDataURL('image/png');

    setSigning(true);
    try {
      // Submit data first
      await handleSubmitData();

      // Then sign
      const res = await axios.post(`${API_URL}/quotes/public/${token}/sign`, {
        signatureDataUrl,
        signedBy: formData.contactName || formData.orgName,
      });

      setPdfUrl(res.data.pdfUrl || null);
      // Skip Tranzila step if quote was marked as no-payment
      setStep(quote?.requirePayment === false ? 11 : 10);
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה בחתימה');
    } finally {
      setSigning(false);
    }
  };

  const goNext = async () => {
    if (step === 2) {
      if (!formData.orgName || !formData.regNumber || !formData.contactName || !formData.contactEmail) {
        alert('אנא מלא את כל שדות החובה');
        return;
      }
    }
    if (step === 3 && !quote?.documentsOptional) {
      const requiredDocs = DOCUMENT_TYPES.filter((d) => d.required);
      const missing = requiredDocs.filter((d) => !uploadedDocs[d.id]);
      if (missing.length > 0) {
        alert(`חסרים מסמכים חובה: ${missing.map((d) => d.name).join(', ')}`);
        return;
      }
    }
    if (step === 4) {
      if (!gamafFilled) {
        alert('אנא מלא את טופס גמא ולאחר מכן סמן את התיבה');
        return;
      }
    }
    if (step === 5) {
      if (!termsAccepted) {
        alert('אנא אשר את תנאי השירות');
        return;
      }
    }
    // Step 6: donor payment add-ons — no validation required (all 3 are optional)
    if (step === 7) {
      if (!orgSettings.receiptAuthorizationGranted) {
        alert('יש לאשר את הרשאת הקבלות וחשבוניות המס');
        return;
      }
      if (!orgSettings.receiptStartNumber || orgSettings.receiptStartNumber < 1) {
        alert('יש להזין מספר התחלה לסדרת הקבלות');
        return;
      }
      if (orgSettings.issuesServiceReceipts && (!orgSettings.serviceReceiptStartNumber || orgSettings.serviceReceiptStartNumber < 1)) {
        alert('יש להזין מספר התחלה לקבלות על שירותים');
        return;
      }
    }
    // Skip documents (3) + Gama form (4) entirely when the quote disables them
    if (quote?.skipGamaAndDocs && step === 2) {
      setStep(5);
      return;
    }
    setStep((s) => s + 1);
  };

  const goBack = () => setStep((s) => (quote?.skipGamaAndDocs && s === 5 ? 2 : s - 1));

  // Countdown timer
  const daysLeft = quote
    ? Math.max(0, Math.ceil((new Date(quote.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center bg-white rounded-2xl p-10 shadow-lg max-w-sm">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">ההצעה לא נמצאה</h2>
          <p className="text-gray-500 text-sm">{error || 'הקישור אינו תקין או שהתוקף פג'}</p>
        </div>
      </div>
    );
  }

  if (quote.status === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center bg-white rounded-2xl p-10 shadow-lg max-w-sm">
          <div className="text-5xl mb-4">⏰</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">ההצעה פגה תוקף</h2>
          <p className="text-gray-500 text-sm">אנא פנה אלינו לקבלת הצעה מחודשת</p>
          <a href="mailto:info@amutech.co.il" className="mt-4 inline-block text-blue-600 hover:underline text-sm">
            info@amutech.co.il
          </a>
        </div>
      </div>
    );
  }

  if (quote.status === 'signed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" dir="rtl">
        <div className="max-w-md w-full">
          {/* Success header */}
          <div className="bg-gradient-to-l from-blue-700 to-indigo-800 rounded-2xl p-8 text-center text-white mb-6 shadow-xl">
            <div className="text-5xl mb-3">✅</div>
            <h1 className="text-2xl font-black">ההצעה נחתמה!</h1>
            <p className="text-blue-200 mt-1">כל הכבוד — שלב 1 הושלם</p>
          </div>

          {/* Steps */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
            <h2 className="font-bold text-gray-800 mb-4">תהליך ההצטרפות</h2>

            {/* Step 1 - Done */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">✓</span>
              </div>
              <div>
                <div className="font-semibold text-gray-800">חתימה על הצעת המחיר</div>
                <div className="text-sm text-green-600">הושלם ✓</div>
              </div>
            </div>

            {/* Quote sent by email */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-lg">📧</span>
              </div>
              <div>
                <div className="font-bold text-blue-800">הצעת המחיר נשלחה אליך במייל</div>
                <div className="text-sm text-blue-600 mt-1">
                  עותק חתום של ההצעה נשלח לתיבת הדואר שלך לשמירה.
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            שאלות? <a href="mailto:support@amutech.co.il" className="text-blue-600 hover:underline">support@amutech.co.il</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <img src="/amutech-logo.png" alt="Amutech" className="h-12 object-contain flex-shrink-0" />
            <span className="text-base md:text-3xl font-black text-blue-800 tracking-wide truncate" style={{ fontFamily: "'Secular One', sans-serif" }}>משקיעים באנשים שעושים טוב</span>
          </div>
          {step < 7 && (
            <span className="text-xs text-gray-500">
              שלב {quote.skipGamaAndDocs && step >= 5 ? step - 2 : step} מתוך {TOTAL_STEPS - 1 - (quote.skipGamaAndDocs ? 2 : 0)}
            </span>
          )}
        </div>
        {/* Progress bar */}
        {step < 7 && (
          <div className="h-1 bg-gray-100">
            <div
              className="h-1 bg-blue-500 transition-all duration-500"
              style={{ width: `${(((quote.skipGamaAndDocs && step >= 5 ? step - 2 : step) - 1) / (TOTAL_STEPS - 2 - (quote.skipGamaAndDocs ? 2 : 0))) * 100}%` }}
            />
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* ─── Step 1: Welcome ─────────────────────────────────────────────── */}
        {step === 1 && (() => {
          const meta = PLAN_META[quote.planId] || PLAN_META.pro;
          const sms = quote.customSms ?? meta.sms;
          const emails = quote.customEmails ?? meta.emails;
          const users = quote.customUsers ?? meta.users;
          const receipts = quote.customReceipts ?? meta.receipts;
          const contacts = quote.planId === 'org' && !quote.customReceipts ? 'ללא הגבלה' : meta.contacts;

          return (
            <div>
              {/* Hero */}
              <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 rounded-2xl shadow-2xl overflow-hidden mb-5 text-white">
                <div className="px-6 pt-3 pb-6 relative">
                  {/* Timer - top left (RTL) */}
                  <div className="flex justify-start mb-0">
                    <div className={`text-xs px-3 py-1.5 rounded-full font-semibold ${
                      daysLeft <= 3 ? 'bg-red-400/80' : 'bg-white/20'
                    }`}>
                      ⏰ {daysLeft === 0 ? 'פג היום!' : `${daysLeft} ימים`}
                    </div>
                  </div>
                  {/* Title block */}
                  <p className="text-white text-2xl md:text-4xl font-extrabold text-center mt-2 mb-1 tracking-wide drop-shadow relative z-10">✨ הצעת מחיר אישית עבור ✨</p>
                  <h1 className="text-3xl md:text-5xl font-black mb-4 text-center relative z-10">{quote.targetOrgName}</h1>


                  {/* Price highlight */}
                  {(() => {
                    const dMonths = quote.discountMonths || 0;
                    const isFreeFirst = quote.discount === 100 && dMonths > 0;
                    const isTempDiscount = quote.discount > 0 && quote.discount < 100 && dMonths > 0;
                    const isPermDiscount = quote.discount > 0 && dMonths === 0;
                    const monthsLabel = (n: number) => n === 1 ? 'החודש הראשון' : `${n} החודשים הראשונים`;
                    const afterLabel = (n: number) => n === 1 ? 'לאחר החודש הראשון' : `לאחר ${n} חודשים`;
                    // Annual estimate: mix free/discounted period with full-price remainder
                    const annual = isFreeFirst
                      ? quote.monthlyPrice * Math.max(0, 12 - dMonths)
                      : isTempDiscount
                        ? quote.finalPrice * dMonths + quote.monthlyPrice * Math.max(0, 12 - dMonths)
                        : quote.finalPrice * 12;
                    return (
                      <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
                        <div className="flex items-end justify-between">
                          <div>
                            <div className="text-blue-200 text-sm mb-1">חבילת {quote.planName}</div>
                            {quote.discount > 0 && (
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-blue-300 line-through text-sm">₪{quote.monthlyPrice}</span>
                                {isFreeFirst ? (
                                  <span className="bg-green-400 text-green-900 text-xs font-bold px-2 py-0.5 rounded-full">
                                    🎉 {monthsLabel(dMonths)} חינם
                                  </span>
                                ) : (
                                  <span className="bg-green-400 text-green-900 text-xs font-bold px-2 py-0.5 rounded-full">
                                    {quote.discount}% הנחה
                                  </span>
                                )}
                                {isTempDiscount && (
                                  <span className="text-blue-200 text-xs">{monthsLabel(dMonths)}</span>
                                )}
                              </div>
                            )}
                            <div className="flex items-baseline gap-1">
                              {isFreeFirst ? (
                                <>
                                  <span className="text-5xl font-black">חינם</span>
                                  <span className="text-blue-200 text-base">· {monthsLabel(dMonths)}</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-5xl font-black">₪{quote.finalPrice.toLocaleString()}</span>
                                  <span className="text-blue-200 text-base">/חודש</span>
                                </>
                              )}
                            </div>
                            <p className="text-xs text-blue-300 mt-1">* המחירים אינם כוללים מע"מ</p>
                            {quote.setupFee != null && quote.setupFee > 0 && (
                              <div className="flex items-center justify-between py-2 border-t border-white/20 mt-2">
                                <span className="text-sm text-blue-200">עלות הקמה חד"פ</span>
                                <span className="text-sm font-bold text-white">₪{quote.setupFee}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right text-blue-200 text-xs">
                            <div>≈ ₪{annual.toLocaleString()}</div>
                            <div>לשנה</div>
                          </div>
                        </div>
                        {(isFreeFirst || isTempDiscount) && (
                          <div className="mt-3 text-xs text-blue-200 bg-white/10 rounded-lg px-3 py-1.5">
                            * {afterLabel(dMonths)}, המחיר יעמוד על ₪{quote.monthlyPrice}/חודש
                          </div>
                        )}
                        {isPermDiscount && (
                          <div className="mt-3 text-xs text-blue-200 bg-white/10 rounded-lg px-3 py-1.5">
                            * הנחה קבועה — המחיר ₪{quote.finalPrice}/חודש לתמיד
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* What's included — numbers */}
              <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                <h2 className="font-bold text-gray-900 mb-4 text-xl">מה כלול בחבילה שלך</h2>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: '📩', label: 'SMS', value: sms.toLocaleString(), sub: meta.smsLabel },
                    { icon: '📧', label: 'מיילים', value: emails.toLocaleString(), sub: meta.smsLabel === 'חד פעמי' ? 'חד פעמי' : 'חודשי' },
                    { icon: '👥', label: 'משתמשים', value: users.toString(), sub: 'עם מידור הרשאות' },
                    { icon: '🧾', label: 'קבלות', value: receipts.toLocaleString(), sub: 'לחודש' },
                    { icon: '📋', label: 'אנשי קשר', value: contacts, sub: 'במאגר' },
                    { icon: '💬', label: 'WhatsApp', value: 'שילוב מלא', sub: 'התממשקות' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl p-4 border-2 border-blue-100 bg-blue-50">
                      <div className="text-3xl mb-1">{item.icon}</div>
                      <div className="text-2xl font-black text-blue-700">{item.value}</div>
                      <div className="text-sm font-semibold text-gray-700">{item.label}</div>
                      <div className="text-sm text-gray-500">{item.sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature list */}
              <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                <h2 className="font-bold text-gray-900 mb-4 text-xl">מה כלול בחבילה</h2>
                <div className="grid grid-cols-1 gap-1">
                  {(quote.customFeatures && quote.customFeatures.length > 0
                    ? quote.customFeatures
                    : meta.features.map((t, i) => ({ id: String(i), text: t, included: true }))
                  ).map((f) => (
                    <div key={f.id} className={`flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0 ${!f.included ? 'opacity-40' : ''}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        f.included ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <span className={`text-sm font-bold ${f.included ? 'text-green-600' : 'text-gray-400'}`}>
                          {f.included ? '✓' : '✗'}
                        </span>
                      </div>
                      <span className={`text-lg ${f.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Onboarding preview */}
              <div className="bg-white rounded-2xl shadow-lg p-5 mb-5">
                <h2 className="font-bold text-gray-900 mb-4 text-xl">🚀 תהליך ההצטרפות</h2>
                <div className="relative">
                  <div className="absolute right-5 top-4 bottom-4 w-0.5 bg-blue-100" />
                  {[
                    { n: 1, t: 'חתימה על הסכם', d: 'יום אחד' },
                    { n: 2, t: 'הגשת מסמכים', d: '1-3 ימי עסקים' },
                    { n: 3, t: 'הקמת הסביבה', d: 'עד 48 שעות' },
                    { n: 4, t: 'ייבוא נתונים', d: '1-2 ימי עסקים' },
                    { n: 5, t: 'הדרכה', d: 'Zoom 60-90 דקות' },
                    { n: 6, t: 'עלייה לאוויר', d: 'עד 7 ימים מרגע החתימה' },
                  ].map((s) => (
                    <div key={s.n} className="flex items-center gap-4 pr-2 mb-4 last:mb-0">
                      <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-base font-black flex-shrink-0 z-10 shadow-sm">
                        {s.n}
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3">
                        <div className="text-lg font-semibold text-gray-800">{s.t}</div>
                        <div className="text-base text-gray-500">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom notes */}
              {quote.customNotes && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span>📝</span>
                    <span className="font-semibold text-amber-800 text-lg">הערה מיוחדת</span>
                  </div>
                  <p className="text-lg text-amber-700">{quote.customNotes}</p>
                </div>
              )}

              {/* Expiry warning */}
              <div className={`rounded-2xl p-4 mb-5 text-center text-lg font-medium ${
                daysLeft <= 3 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-orange-50 text-orange-700 border border-orange-200'
              }`}>
                ⏰ ההצעה תקפה עד {new Date(quote.expiresAt).toLocaleDateString('he-IL')}
                {daysLeft > 0 && ` (עוד ${daysLeft} ימים)`}
              </div>

              <button
                onClick={goNext}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black py-6 rounded-2xl text-2xl transition-all shadow-lg hover:shadow-xl"
              >
                אני רוצה להצטרף! ←
              </button>
            </div>
          );
        })()}

        {/* ─── Step 2: Org Details ─────────────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">פרטי העמותה</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">שם העמותה *</label>
                <input
                  type="text"
                  value={formData.orgName}
                  onChange={(e) => setFormData({ ...formData, orgName: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="שם מלא של העמותה"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">מספר עמותה ברשם *</label>
                <input
                  type="text"
                  value={formData.regNumber}
                  onChange={(e) => setFormData({ ...formData, regNumber: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="580XXXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">כתובת</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="כתובת העמותה"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">שם איש קשר *</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="שם מלא"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">טלפון</label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="05X-XXXXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">אימייל *</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.org.il"
                />
              </div>
            </div>

            <NavButtons onBack={goBack} onNext={goNext} />
          </div>
        )}

        {/* ─── Step 3: Documents ───────────────────────────────────────────── */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">מסמכים{quote?.documentsOptional ? '' : ' נדרשים'}</h2>
            <p className="text-sm text-gray-500 mb-6">
              {quote?.documentsOptional
                ? 'ניתן להעלות את המסמכים הבאים — אך אין חובה, וניתן לדלג ולהמשיך'
                : 'העלה את המסמכים הבאים להשלמת ההצטרפות'}
            </p>

            {quote?.documentsOptional && (
              <div className="mb-4 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2">
                העלאת המסמכים אינה חובה בהצעה זו — ניתן להמשיך גם ללא העלאה.
              </div>
            )}

            <div className="space-y-3">
              {DOCUMENT_TYPES.map((doc) => {
                const uploaded = uploadedDocs[doc.id];
                const isUploading = uploadingDoc === doc.id;
                const isRequired = doc.required && !quote?.documentsOptional;
                return (
                  <div
                    key={doc.id}
                    className={`border-2 rounded-xl p-4 transition-all ${
                      uploaded ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {uploaded ? (
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        ) : (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isRequired ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-400'
                          }`}>
                            <span className="text-xs">{isRequired ? '!' : '?'}</span>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-800">{doc.name}</div>
                          {uploaded && (
                            <div className="text-xs text-green-600 truncate max-w-[180px]">{uploaded.name}</div>
                          )}
                          {!isRequired && !uploaded && (
                            <div className="text-xs text-gray-400">לא חובה</div>
                          )}
                        </div>
                      </div>

                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleDocUpload(doc.id, file);
                          }}
                        />
                        <span
                          className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                            isUploading
                              ? 'bg-gray-100 text-gray-400 cursor-wait'
                              : uploaded
                              ? 'bg-white border border-green-300 text-green-600 hover:bg-green-50'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isUploading ? 'מעלה...' : uploaded ? 'החלף' : 'העלה'}
                        </span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <NavButtons onBack={goBack} onNext={goNext} />
          </div>
        )}

        {/* ─── Step 4: GAMAF ───────────────────────────────────────────────── */}
        {step === 4 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">מילוי טופס גמא</h2>
            <p className="text-sm text-gray-500 mb-6">
              על מנת להשלים את ההצטרפות, יש למלא את טופס גמא.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 text-center">
              <div className="text-3xl mb-3">📋</div>
              <h3 className="font-bold text-gray-900 mb-2">טופס גמא</h3>
              <p className="text-sm text-gray-600 mb-4">
                לחץ על הכפתור להמשיך למילוי הטופס. הטופס ייפתח בחלון חדש.
              </p>
              <a
                href="https://mm.gamaf.co.il/?skipLanding=true&affiliate=amutech"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#E8490F] hover:bg-[#cf3e0b] text-white font-bold px-8 py-3 rounded-xl transition-colors text-base"
              >
                מלא טופס גמא ←
              </a>
            </div>

            <label className="flex items-center gap-3 cursor-pointer bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-colors">
              <input
                type="checkbox"
                checked={gamafFilled}
                onChange={(e) => setGamafFilled(e.target.checked)}
                className="w-5 h-5 rounded text-blue-600 flex-shrink-0"
              />
              <span className="text-sm font-medium text-gray-700">
                ✅ מילאתי את טופס גמא
              </span>
            </label>

            <NavButtons onBack={goBack} onNext={goNext} />
          </div>
        )}

        {/* ─── Step 5: Onboarding + Terms ──────────────────────────────────── */}
        {step === 5 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">תהליך ההצטרפות</h2>
            <p className="text-sm text-gray-500 mb-5">כך נראה תהליך ההצטרפות שלנו:</p>

            <div className="space-y-3 mb-6">
              {[
                { step: 1, title: 'חתימה על הסכם', time: 'יום אחד' },
                { step: 2, title: 'הגשת מסמכים', time: '1-3 ימי עסקים' },
                { step: 3, title: 'הקמת הסביבה', time: 'עד 48 שעות' },
                { step: 4, title: 'ייבוא נתונים קיימים', time: '1-2 ימי עסקים' },
                { step: 5, title: 'הדרכה', time: 'שיחת Zoom 60-90 דקות' },
                { step: 6, title: 'עלייה לאוויר', time: 'עד 7 ימי עסקים מרגע החתימה' },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {s.step}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm">שלב {s.step} — {s.title}</div>
                    <div className="text-xs text-gray-500">{s.time}</div>
                  </div>
                </div>
              ))}
            </div>

            <label className="flex items-start gap-3 cursor-pointer bg-blue-50 border border-blue-200 rounded-xl p-4 hover:border-blue-400 transition-colors">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-5 h-5 rounded text-blue-600 mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-gray-700">
                אני מאשר/ת את תנאי השירות ותהליך ההצטרפות של Amutech, כפי שמפורט לעיל.
              </span>
            </label>

            <NavButtons onBack={goBack} onNext={goNext} />
          </div>
        )}

        {/* ─── Step 6: Donor Payment Add-ons ───────────────────────────────── */}
        {step === 6 && (() => {
          const addonCount = (donorPaymentMethods.googlePay ? 1 : 0) + (donorPaymentMethods.applePay ? 1 : 0) + (donorPaymentMethods.bit ? 1 : 0);
          const addonCost = addonCount * ADDON_PRICE;
          return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">אמצעי סליקה לתורמים שלך</h2>
              <p className="text-sm text-gray-500 mb-6 text-center">
                סמן/י אילו אמצעי סליקה תרצה/י להציע לתורמים שלך — תוספת של ₪{ADDON_PRICE}/חודש לכל אמצעי.
              </p>

              <div className="space-y-3 mb-4">
                {DONOR_PAYMENT_ADDONS.map((m) => {
                  const enabled = (donorPaymentMethods as any)[m.id];
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setDonorPaymentMethods((prev) => ({ ...prev, [m.id]: !enabled }))}
                      className={`w-full text-right p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                        enabled
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-2xl flex-shrink-0">{m.icon || '💳'}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-bold text-sm ${enabled ? 'text-blue-900' : 'text-gray-900'}`}>
                          {m.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{m.desc}</div>
                      </div>
                      <div className="text-sm font-semibold text-gray-700 whitespace-nowrap">+₪{ADDON_PRICE}/חודש</div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        enabled ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {enabled && <span className="text-white text-xs font-black">✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-700 flex items-center justify-between">
                <span>סה"כ תוספת חודשית</span>
                <span className="font-bold text-blue-700">₪{addonCost}/חודש</span>
              </div>

              <NavButtons onBack={goBack} onNext={goNext} />
            </div>
          );
        })()}

        {/* ─── Step 7: Org Settings (logo, website, receipts) ──────────────── */}
        {step === 7 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">הגדרות העמותה</h2>
            <p className="text-sm text-gray-500 mb-6 text-center">פרטים שיופיעו בקבלות, באתר וב-Amutech</p>

            <div className="space-y-4">
              {/* Logo upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">לוגו העמותה (אופציונלי)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setOrgSettings((s) => ({ ...s, logo: ev.target?.result as string }));
                    reader.readAsDataURL(file);
                  }}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {orgSettings.logo && (
                  <img src={orgSettings.logo} alt="לוגו" className="mt-2 h-16 object-contain rounded-lg border border-gray-200 bg-white p-1" />
                )}
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">כתובת האתר (אופציונלי)</label>
                <input
                  type="url"
                  value={orgSettings.websiteUrl}
                  onChange={(e) => setOrgSettings((s) => ({ ...s, websiteUrl: e.target.value }))}
                  placeholder="https://www.amuta.org.il"
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Receipt numbering */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-sm text-blue-900 mb-1">מספור קבלות וחשבוניות</h3>
                <p className="text-xs text-blue-700 mb-3">אם יש לכם מספור קודם — המשיכו ממנו. אחרת השאירו את ברירות המחדל.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">קבלה על תרומה</label>
                    <input
                      type="number"
                      min={1}
                      value={orgSettings.receiptStartNumber}
                      onChange={(e) => setOrgSettings((s) => ({ ...s, receiptStartNumber: Number(e.target.value) }))}
                      dir="ltr"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">קבלה על זיכוי</label>
                    <input
                      type="number"
                      min={1}
                      value={orgSettings.creditNoteStartNumber}
                      onChange={(e) => setOrgSettings((s) => ({ ...s, creditNoteStartNumber: Number(e.target.value) }))}
                      dir="ltr"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Service receipts (not donations) */}
                <label className="flex items-start gap-3 cursor-pointer mt-3 bg-white border border-blue-200 rounded-lg p-3 hover:border-blue-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={orgSettings.issuesServiceReceipts}
                    onChange={(e) => setOrgSettings((s) => ({ ...s, issuesServiceReceipts: e.target.checked }))}
                    className="w-5 h-5 rounded text-blue-600 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">
                    העמותה מוציאה גם <strong>קבלות על שירותים</strong> (ולא רק על תרומות)
                  </span>
                </label>
                {orgSettings.issuesServiceReceipts && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">מספור קבלה על שירותים</label>
                    <input
                      type="number"
                      min={1}
                      value={orgSettings.serviceReceiptStartNumber}
                      onChange={(e) => setOrgSettings((s) => ({ ...s, serviceReceiptStartNumber: Number(e.target.value) }))}
                      dir="ltr"
                      className="w-full sm:w-1/2 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Fill-receipts authorization */}
              <label className="flex items-start gap-3 cursor-pointer bg-yellow-50 border border-yellow-200 rounded-xl p-4 hover:border-yellow-400 transition-colors">
                <input
                  type="checkbox"
                  checked={orgSettings.receiptAuthorizationGranted}
                  onChange={(e) => setOrgSettings((s) => ({ ...s, receiptAuthorizationGranted: e.target.checked }))}
                  className="w-5 h-5 rounded text-blue-600 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-gray-700">
                  <strong>אני מאשר/ת ל-Amutech</strong> למלא בעבורי את פרטי הקבלות וחשבוניות המס במערכת חברת הסליקה (Hyp/Tranzila), כולל מספרי תחילה, פרטי העמותה ושדות חובה.
                </span>
              </label>
            </div>

            <NavButtons onBack={goBack} onNext={goNext} />
          </div>
        )}

        {/* ─── Step 8: Price Summary ───────────────────────────────────────── */}
        {step === 8 && (() => {
          const addonCount = (donorPaymentMethods.googlePay ? 1 : 0) + (donorPaymentMethods.applePay ? 1 : 0) + (donorPaymentMethods.bit ? 1 : 0);
          const addonCost = addonCount * ADDON_PRICE;
          const enabledAddons = DONOR_PAYMENT_ADDONS.filter(a => (donorPaymentMethods as any)[a.id]);

          const oneTime = quote?.oneTimePayment === true;
          const fullMonthly = (quote?.monthlyPrice || 0) + addonCost;            // before discount
          const discountedMonthly = (quote?.finalPrice || 0) + addonCost;        // after discount
          const discount = quote?.discount || 0;
          const discountMonths = quote?.discountMonths || 0;                     // 0 = permanent
          const isFreeFirst = !oneTime && discount === 100 && discountMonths > 0;
          const hasTempDiscount = !oneTime && discount > 0 && discount < 100 && discountMonths > 0;
          const hasPermDiscount = !oneTime && discount > 0 && discountMonths === 0;

          const setupFee = quote?.setupFee || 0;
          const requirePayment = quote?.requirePayment !== false;

          // Compute first-charge: 0 if free first month, else discountedMonthly. Plus setup.
          const firstMonthCost = isFreeFirst ? 0 : discountedMonthly;
          const firstCharge = firstMonthCost + setupFee;

          // ── One-time payment quotes: a single flat charge, no monthly /
          // recurring framing at all ──────────────────────────────────────────
          if (oneTime) {
            // One-time = pay ONLY the setup fee now. The plan + add-ons are billed later.
            const hasNowCharge = requirePayment && setupFee > 0;
            const oneTimeVat = Math.round(setupFee * 0.18);
            const oneTimeGross = setupFee + oneTimeVat;
            return (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">סיכום מחירים</h2>
                <p className="text-sm text-gray-500 mb-5 text-center">תשלום חד פעמי — דמי הקמה בלבד</p>

                {/* Pay now: setup fee only */}
                {hasNowCharge && (
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">דמי הקמה (חד-פעמי)</span>
                      <span className="font-semibold">₪{setupFee}</span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100">
                      <span className="text-gray-500">מע"מ (18%)</span>
                      <span className="text-gray-600">₪{oneTimeVat}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-t-2 border-gray-200 mt-1">
                      <span className="font-bold text-gray-900">סה"כ לתשלום עכשיו (כולל מע"מ)</span>
                      <span className="font-bold text-gray-900 text-base">₪{oneTimeGross}</span>
                    </div>
                  </div>
                )}

                {/* Billed later: plan + donor add-ons — NOT charged now */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                  <div className="text-sm font-semibold text-amber-900">יחויב בהמשך — לא נגבה כעת</div>
                  <div className="text-xs text-amber-700 mb-2">התשלום על החבילה יתבצע בהמשך בנפרד.</div>
                  <div className="flex items-center justify-between text-xs text-amber-900 py-0.5">
                    <span>חבילת {quote?.planName}</span>
                    <span>
                      {discount > 0 && <span className="line-through text-amber-400 ml-1">₪{quote?.monthlyPrice}</span>}
                      ₪{quote?.finalPrice}/חודש
                    </span>
                  </div>
                  {enabledAddons.map(a => (
                    <div key={a.id} className="flex items-center justify-between text-xs text-amber-900 py-0.5">
                      <span>{a.name} (אמצעי תשלום לתורמים)</span>
                      <span>₪{ADDON_PRICE}/חודש</span>
                    </div>
                  ))}
                </div>

                {hasNowCharge ? (
                  <div className="bg-gradient-to-l from-indigo-700 to-indigo-900 text-white rounded-xl p-5 mb-4">
                    <div className="text-xs opacity-80 mb-1">תשלום חד פעמי — דמי הקמה (כולל מע"מ)</div>
                    <div className="text-3xl font-black">₪{oneTimeGross}</div>
                    <div className="text-xs opacity-80 mt-2 space-y-0.5">
                      <div>• דמי הקמה: ₪{setupFee}</div>
                      <div>• מע"מ (18%): ₪{oneTimeVat}</div>
                      <div className="opacity-90 pt-1">תשלום החבילה יבוצע בהמשך — לא נגבה כעת.</div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">📨</span>
                      <div>
                        <div className="font-bold text-blue-900 text-sm">חיוב יסוכם איתך בנפרד</div>
                        <div className="text-xs text-blue-700 mt-0.5">אין תשלום באתר כעת. נחזור אליך בנושא החיוב.</div>
                      </div>
                    </div>
                  </div>
                )}

                <NavButtons onBack={goBack} onNext={goNext} nextLabel="המשך לחתימה ←" />
              </div>
            );
          }

          return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">סיכום מחירים</h2>
              <p className="text-sm text-gray-500 mb-5 text-center">בדוק/י את הפירוט המלא לפני החתימה</p>

              {/* Itemized breakdown */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-700">חבילת {quote?.planName}</span>
                  <span className="font-semibold">₪{quote?.monthlyPrice}/חודש</span>
                </div>
                {enabledAddons.map(a => (
                  <div key={a.id} className="flex items-center justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-600">+ {a.name} (אמצעי תשלום לתורמים)</span>
                    <span className="text-gray-700">₪{ADDON_PRICE}/חודש</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-3 border-t-2 border-gray-200 mt-2">
                  <span className="font-bold text-gray-900">מחיר מלא לחודש</span>
                  <span className="font-bold text-gray-900 text-base">₪{fullMonthly}/חודש</span>
                </div>
              </div>

              {/* Discount/free callouts */}
              {isFreeFirst && (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">🎉</span>
                    <span className="font-bold text-green-900">{discountMonths === 1 ? 'חודש ראשון חינם!' : `${discountMonths} חודשים ראשונים חינם!`}</span>
                  </div>
                  <p className="text-xs text-green-700">לאחר תקופת ההטבה, החיוב החודשי יהיה ₪{fullMonthly}/חודש.</p>
                </div>
              )}

              {hasTempDiscount && (
                <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🏷️</span>
                    <span className="font-bold text-purple-900">הנחה של {discount}% ל-{discountMonths} {discountMonths === 1 ? 'חודש' : 'חודשים'} ראשונים</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white rounded-lg p-2.5 text-center">
                      <div className="text-xs text-gray-500">בתקופת ההטבה</div>
                      <div className="font-bold text-purple-700">₪{discountedMonthly}/חודש</div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 text-center">
                      <div className="text-xs text-gray-500">אחר כך</div>
                      <div className="font-bold text-gray-700">₪{fullMonthly}/חודש</div>
                    </div>
                  </div>
                </div>
              )}

              {hasPermDiscount && (
                <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🏷️</span>
                    <div>
                      <div className="font-bold text-purple-900">הנחה קבועה {discount}%</div>
                      <div className="text-xs text-purple-700 line-through">₪{fullMonthly}/חודש</div>
                      <div className="font-bold text-purple-900">₪{discountedMonthly}/חודש לתמיד</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Setup fee (only relevant if payment required) */}
              {requirePayment && setupFee > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-amber-900">דמי הקמה (חד-פעמי)</span>
                    <span className="font-bold text-amber-900">₪{setupFee}</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">יתווסף לתשלום הראשון בלבד</p>
                </div>
              )}

              {/* First charge box */}
              {requirePayment ? (
                <div className="bg-gradient-to-l from-blue-700 to-indigo-800 text-white rounded-xl p-5 mb-4">
                  <div className="text-xs opacity-80 mb-1">תשלום ראשון</div>
                  <div className="text-3xl font-black">₪{firstCharge}</div>
                  <div className="text-xs opacity-80 mt-2 space-y-0.5">
                    {setupFee > 0 && <div>• דמי הקמה: ₪{setupFee}</div>}
                    {isFreeFirst
                      ? <div>• חודש ראשון: חינם 🎉</div>
                      : hasTempDiscount
                        ? <div>• חודש ראשון: ₪{discountedMonthly} (כולל {discount}% הנחה)</div>
                        : <div>• חודש ראשון: ₪{discountedMonthly}</div>}
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">📨</span>
                    <div>
                      <div className="font-bold text-blue-900 text-sm">חיוב יסוכם איתך בנפרד</div>
                      <div className="text-xs text-blue-700 mt-0.5">לא נדרש תשלום באתר. נחזור אליך בנושא החיוב.</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Future months recurring */}
              {(hasTempDiscount || isFreeFirst) ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{isFreeFirst ? `מחודש ${discountMonths + 1} ואילך` : `מחודש ${discountMonths + 1} ואילך`}</span>
                    <span className="font-bold text-gray-900">₪{fullMonthly}/חודש</span>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm flex items-center justify-between">
                  <span className="text-gray-700">חיוב חודשי קבוע מהחודש הבא</span>
                  <span className="font-bold text-gray-900">₪{discountedMonthly}/חודש</span>
                </div>
              )}

              <NavButtons onBack={goBack} onNext={goNext} nextLabel="המשך לחתימה ←" />
            </div>
          );
        })()}

        {/* ─── Step 9: Signature ───────────────────────────────────────────── */}
        {step === 9 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">חתימה דיגיטלית</h2>
            <p className="text-sm text-gray-500 mb-5">חתום/י כאן בעכבר, אצבע או עט</p>

            <div className="border-2 border-gray-300 rounded-xl overflow-hidden mb-3 bg-white relative">
              <canvas
                ref={canvasRef}
                width={560}
                height={150}
                className="w-full touch-none cursor-crosshair"
                style={{ display: 'block' }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={stopDraw}
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-gray-300 text-sm">חתום כאן...</span>
                </div>
              )}
            </div>

            <button
              onClick={clearSignature}
              className="text-sm text-gray-500 hover:text-gray-700 underline mb-6"
            >
              נקה חתימה
            </button>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 text-sm text-gray-600">
              <p>
                בלחיצה על "חתום ואשר" אני מאשר/ת שקראתי את ההצעה, מסכים/ה לתנאים, ומאשר/ת את
                ביצוע ההתקשרות עם Amutech.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={goBack}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                חזרה
              </button>
              <button
                onClick={handleSign}
                disabled={signing || !hasSignature}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50 text-sm"
              >
                {signing ? 'חותם...' : 'חתום ואשר ✓'}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 10: Tranzila Payment + Save Card ───────────────────────── */}
        {step === 10 && (() => {
          const addonCount = (donorPaymentMethods.googlePay ? 1 : 0) + (donorPaymentMethods.applePay ? 1 : 0) + (donorPaymentMethods.bit ? 1 : 0);
          const dMonths = quote?.discountMonths || 0;
          const oneTime = quote?.oneTimePayment === true;
          const isFreeFirst = !oneTime && quote?.discount === 100 && dMonths > 0;
          const monthlyTotal = (quote?.finalPrice || 0) + (oneTime ? 0 : addonCount * ADDON_PRICE);
          const firstMonthCost = isFreeFirst ? 0 : monthlyTotal;
          const setupFee = quote?.setupFee || 0;
          // One-time charges ONLY the setup fee now; plan + add-ons are billed later.
          const firstCharge = oneTime ? setupFee : (firstMonthCost + setupFee);
          const oneTimeVat = Math.round(setupFee * 0.18);
          const oneTimeGross = setupFee + oneTimeVat;

          const handlePay = async () => {
            setPaying(true);
            try {
              const res = await axios.post(`${API_URL}/quotes/public/${token}/payment-session`);
              const d = res.data || {};
              const monthlyGross = Number(d.monthlyGross) || 0;
              const monthlyNet = Number(d.monthlyNet) || 0;
              const subline = oneTime
                ? 'תשלום חד פעמי — דמי הקמה בלבד (תשלום החבילה בהמשך)'
                : d.isFreeFirst
                ? 'חודש ראשון חינם — חיוב חודשי יתחיל מהחודש הבא'
                : `מהחודש הבא: ₪${monthlyGross}/חודש (כולל מע"מ — ₪${monthlyNet} + 18%)`;

              // Build a per-item breakdown (pre-VAT amounts) so the customer
              // sees exactly what's being charged: setup fee, monthly plan,
              // and any donor-payment add-ons (Apple Pay / Google Pay / Bit).
              const lines: Array<{ label: string; amount: number; note?: string }> = [];
              if (setupFee > 0) {
                lines.push({ label: 'דמי הקמה', amount: setupFee, note: 'חד-פעמי' });
              }
              const periodNote = oneTime ? 'תשלום חד פעמי' : 'חודש ראשון';
              // One-time charges only the setup fee — the plan is billed later, not now.
              if (!isFreeFirst && !oneTime) {
                lines.push({
                  label: `חבילת ${quote?.planName || ''}`,
                  amount: quote?.finalPrice || 0,
                  note: periodNote,
                });
              }
              // One-time quotes don't charge the monthly donor-payment add-ons here.
              if (!oneTime && donorPaymentMethods.applePay) {
                lines.push({ label: 'Apple Pay — אמצעי תשלום לתורמים', amount: ADDON_PRICE, note: periodNote });
              }
              if (!oneTime && donorPaymentMethods.googlePay) {
                lines.push({ label: 'Google Pay — אמצעי תשלום לתורמים', amount: ADDON_PRICE, note: periodNote });
              }
              if (!oneTime && donorPaymentMethods.bit) {
                lines.push({ label: 'Bit — אמצעי תשלום לתורמים', amount: ADDON_PRICE, note: periodNote });
              }

              setIframeDetails({
                item: d.description || `תשלום ראשון — ${quote?.planName || ''}`,
                lines,
                subline,
                net: Number(d.net) || 0,
                vat: Number(d.vat) || 0,
                gross: Number(d.gross) || 0,
              });
              setIframeUrl(d.url || d.iframeUrl);
              setIframeOpen(true);
            } catch (err: any) {
              alert(err.response?.data?.error || 'שגיאה ביצירת טופס התשלום');
            } finally {
              setPaying(false);
            }
          };

          return (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">{oneTime ? 'תשלום חד פעמי' : 'תשלום ראשון'}</h2>
              <p className="text-sm text-gray-500 mb-6 text-center">
                {oneTime
                  ? 'דמי הקמה בלבד — תשלום החבילה יבוצע בהמשך'
                  : (setupFee > 0 ? 'חיוב הקמה + חודש ראשון' : 'חיוב חודש ראשון')}
              </p>

              <div className="bg-gradient-to-l from-blue-700 to-indigo-800 text-white rounded-2xl p-5 mb-5">
                <div className="text-xs opacity-80 mb-1">{oneTime ? 'סכום לחיוב היום (כולל מע"מ)' : 'סכום לחיוב היום'}</div>
                <div className="text-3xl font-black">₪{oneTime ? oneTimeGross : firstCharge}</div>
                <div className="text-xs opacity-80 mt-2 space-y-0.5">
                  {setupFee > 0 && <div>• דמי הקמה: ₪{setupFee} (חד-פעמי)</div>}
                  {oneTime
                    ? <>
                        <div>• מע"מ (18%): ₪{oneTimeVat}</div>
                        <div className="opacity-90 pt-1">תשלום החבילה ({quote?.planName}, ₪{quote?.finalPrice}/חודש) והאמצעים יחויב בהמשך — לא נגבה כעת.</div>
                      </>
                    : isFreeFirst
                    ? <div>• חודש ראשון: חינם 🎉</div>
                    : <div>• חיוב חודש ראשון: ₪{monthlyTotal}</div>}
                </div>
              </div>

              {/* Save card for recurring — only for recurring quotes */}
              {!oneTime && (
                <label className="flex items-start gap-3 cursor-pointer bg-blue-50 border border-blue-200 rounded-xl p-4 hover:border-blue-400 transition-colors mb-4">
                  <input
                    type="checkbox"
                    checked={cardSavedForRecurring}
                    onChange={(e) => setCardSavedForRecurring(e.target.checked)}
                    className="w-5 h-5 rounded text-blue-600 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">
                    <strong>אני מאשר/ת לשמור את פרטי האשראי</strong> לחיובים החודשיים הבאים. ניתן לבטל בכל עת מתוך המערכת.
                  </span>
                </label>
              )}

              <button
                onClick={handlePay}
                disabled={paying}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl text-base transition-colors"
              >
                {paying ? 'טוען טופס תשלום…' : `🔒 פתח טופס תשלום מאובטח ←`}
              </button>

              <p className="text-xs text-gray-400 text-center mt-3">
                מאובטח ב-Tranzila · פרטי האשראי לא נשמרים אצלנו
              </p>
            </div>
          );
        })()}

        {/* Tranzila iframe modal — opens after the customer clicks "פתח טופס תשלום" */}
        <PaymentIframeModal
          url={iframeUrl}
          open={iframeOpen}
          onClose={() => setIframeOpen(false)}
          title={`${quote?.oneTimePayment ? 'תשלום חד פעמי' : 'תשלום ראשון'} — Amutech`}
          details={iframeDetails}
          onSuccess={() => {
            setIframeOpen(false);
            setPaymentDone(true);
            setStep(11);
          }}
          onFail={() => {
            setIframeOpen(false);
            alert('התשלום נכשל. נסה שוב או פנה לתמיכה.');
          }}
        />

        {/* ─── Step 11: Success ────────────────────────────────────────────── */}
        {step === 11 && (
          <div className="max-w-md mx-auto">
            {/* Success header — acknowledges payment when one was made */}
            <div className={`rounded-2xl p-8 text-center text-white mb-6 shadow-xl ${
              paymentDone
                ? 'bg-gradient-to-l from-emerald-600 to-green-600'
                : 'bg-gradient-to-l from-blue-700 to-indigo-800'
            }`}>
              <div className="text-5xl mb-3">{paymentDone ? '🎉' : '✅'}</div>
              <h1 className="text-2xl font-black">
                {paymentDone ? 'התשלום עבר בהצלחה!' : 'ההצעה נחתמה!'}
              </h1>
              <p className={`mt-1 ${paymentDone ? 'text-emerald-100' : 'text-blue-200'}`}>
                {paymentDone ? 'תודה — קיבלנו את התשלום הראשון' : 'כל הכבוד — שלב 1 הושלם'}
              </p>
            </div>

            {/* Steps */}
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
              <h2 className="font-bold text-gray-800 mb-4">תהליך ההצטרפות</h2>

              {/* Step 1 - Signed */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">חתימה על הצעת המחיר</div>
                  <div className="text-sm text-green-600">הושלם ✓</div>
                </div>
              </div>

              {/* Step 2 - Payment (only when one was made) */}
              {paymentDone && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">תשלום ראשון</div>
                    <div className="text-sm text-green-600">חויב בהצלחה ✓</div>
                  </div>
                </div>
              )}

              {/* Quote sent by email */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-lg">📧</span>
                </div>
                <div>
                  <div className="font-bold text-blue-800">הצעת המחיר נשלחה אליך במייל</div>
                  <div className="text-sm text-blue-600 mt-1">
                    עותק חתום של ההצעה נשלח לתיבת הדואר שלך לשמירה.
                  </div>
                </div>
              </div>
            </div>

            {/* PDF download if available */}
            {(pdfUrl || quote.pdfUrl) && (() => {
              const url = pdfUrl || quote.pdfUrl || '';
              const handleDownload = () => {
                if (url.startsWith('data:')) {
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `הצעת-מחיר-${quote.targetOrgName}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } else {
                  window.open(url, '_blank');
                }
              };
              return (
                <div className="mb-4">
                  <button
                    onClick={handleDownload}
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors"
                  >
                    הורד PDF ←
                  </button>
                </div>
              );
            })()}

            <p className="text-center text-sm text-gray-500">
              שאלות? <a href="mailto:support@amutech.co.il" className="text-blue-600 hover:underline">support@amutech.co.il</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Nav buttons component
const NavButtons: React.FC<{ onBack: () => void; onNext: () => void; nextLabel?: string }> = ({
  onBack,
  onNext,
  nextLabel = 'המשך ←',
}) => (
  <div className="flex gap-3 mt-8">
    <button
      onClick={onBack}
      className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
    >
      חזרה
    </button>
    <button
      onClick={onNext}
      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors text-sm"
    >
      {nextLabel}
    </button>
  </div>
);

export default PublicQuote;
